// App.js
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';

import './styles/App.css';

// Firebase imports
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';
import { getStorage } from 'firebase/storage';
import { getFirestore } from 'firebase/firestore';

// Eager load - needed immediately on every page
import Home from './pages/Home';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { AuthProvider } from './contexts/AuthContext';

// Lazy load - only loaded when needed
const Contact = lazy(() => import('./pages/Contact'));
const InfoPage = lazy(() => import('./pages/InfoPage'));
const PricingPage = lazy(() => import('./components/PricingPage'));
const DownloadPage = lazy(() => import('./pages/Download'));
const GeneralTerms = lazy(() => import('./pages/GeneralTerms'));
const Guides = lazy(() => import('./pages/Guides'));
const DeliveryStatus = lazy(() => import('./pages/DeliveryStatus'));
const BusPage = lazy(() => import('./pages/BusPage'));
const NewsLetter = lazy(() => import('./pages/NewsLetter'));
const Flowers = lazy(() => import('./pages/Flowers'));
const ApiPage = lazy(() => import('./pages/ApiPage'));
const DownloadApp = lazy(() => import('./pages/DownloadApp'));
const EcommercePage = lazy(() => import('./pages/Ecommerce'));
const WooCommerce = lazy(() => import('./pages/Ecommerce/WooCommerce'));
const Shopify = lazy(() => import('./pages/Ecommerce/Shopify'));
const NShift = lazy(() => import('./pages/Ecommerce/NShift'));
const LoginForm = lazy(() => import('./components/CreateUser'));
const UserDataSettings = lazy(() => import('./pages/Compliance/UserDataSettings'));
const NotFound = lazy(() => import('./NotFound'));
const UserDeliveries = lazy(() => import('./pages/User/UserDeliveries'));
const DeliveryConfirmationScreen = lazy(() => import('./components/DeliveryConfirmationScreen'));
const DeliveryDetailsScreen = lazy(() => import('./components/DeliveryDetailsScreen'));

// Your Firebase configuration
const firebaseConfig = {
  apiKey: 'AIzaSyCWg8bk28knXsOLxPpKVMRaGoa37tHxpsY',
  authDomain: 'myxline-fin.firebaseapp.com',
  projectId: 'myxline-fin',
  storageBucket: 'myxline-fin.appspot.com',
  messagingSenderId: '626397172965',
  appId: '1:626397172965:web:bd27ee9c6775f86c33b00c',
  measurementId: 'G-MFRZ9ZBYG9',
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
const analytics = getAnalytics(app);

// Custom MUI theme
const theme = createTheme({
  typography: {
    fontFamily: 'sans-serif',
  },
});

// Loading fallback component
const PageLoader = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '50vh' 
  }}>
    <div>Ladataan...</div>
  </div>
);

// External redirect components
function ExternalRedirect() {
  React.useEffect(() => {
    window.location.href = 'https://app.myxline.fi/asiakassivu';
  }, []);
  return null;
}

function ExternalRedirectTerms() {
  React.useEffect(() => {
    window.location.href = 'https://app.myxline.fi/tietosuoja';
  }, []);
  return null;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <Router>
          <Navbar />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* External redirects - no lazy loading needed */}
              <Route path="/asiakassivu" element={<ExternalRedirect />} />
              <Route path="/tietosuoja" element={<ExternalRedirectTerms />} />

              {/* Home - eager loaded */}
              <Route path="/" element={<Home />} />
              
              {/* Marketing routes - reuse Home */}
              <Route path="/laheta" element={<Home />} />
              <Route path="/tilaa" element={<Home />} />
              <Route path="/joulu" element={<Home />} />
              <Route path="/eurolla/:id" element={<Home />} />

              {/* Download */}
              <Route path="/lataa" element={<DownloadApp />} />

              {/* Bus pages */}
              <Route path="/bussi" element={<BusPage />} />
              <Route path="/bussi/:id" element={<BusPage />} />

              {/* Integrations */}
              <Route path="/integrations" element={<ApiPage />} />
              <Route path="/integrations/:id" element={<ApiPage />} />

              {/* Ecommerce */}
              <Route path="/ecommerce" element={<EcommercePage />} />
              <Route path="/ecommerce/:id" element={<EcommercePage />} />
              <Route path="/ecommerce/woocommerce" element={<WooCommerce />} />
              <Route path="/ecommerce/woocommerce/:id" element={<WooCommerce />} />
              <Route path="/demo" element={<Shopify />} />
              <Route path="/esimerkki" element={<Shopify />} />
              <Route path="/ecommerce/shopify" element={<Shopify />} />
              <Route path="/ecommerce/shopify/:id" element={<Shopify />} />
              <Route path="/ecommerce/nshift" element={<NShift />} />
              <Route path="/ecommerce/nshift/:id" element={<NShift />} />

              {/* Delivery flow */}
              <Route path="/d" element={<DeliveryDetailsScreen />} />
              <Route path="/d/:id" element={<DeliveryDetailsScreen />} />
              <Route path="/dc" element={<DeliveryConfirmationScreen />} />
              <Route path="/dc/:id" element={<DeliveryConfirmationScreen />} />
              <Route path="/kuljetus/:id" element={<DeliveryStatus />} />
              <Route path="/vastaanota/:id" element={<DeliveryStatus />} />

              {/* Info pages */}
              <Route path="/yhteystiedot" element={<Contact />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/info" element={<InfoPage />} />
              <Route path="/help" element={<InfoPage />} />
              <Route path="/hinnasto" element={<PricingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/cancel-newsletter/:id" element={<NewsLetter />} />

              {/* Auth & Terms */}
              <Route path="/log-in" element={<DownloadPage />} />
              <Route path="/create-user" element={<DownloadPage />} />
              <Route path="/kayttoehdot" element={<GeneralTerms />} />
              <Route path="/terms-and-conditions" element={<GeneralTerms />} />

              {/* User pages */}
              <Route path="/kirjaudu" element={<LoginForm />} />
              <Route path="/toimitukset" element={<UserDeliveries />} />
              <Route path="/kayttaja-asetukset" element={<UserDataSettings />} />

              {/* Flowers */}
              <Route path="/kukat" element={<Flowers />} />
              <Route path="/kukat/:id" element={<Flowers />} />

              {/* Guides */}
              <Route path="/guides/payments" element={<Guides />} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Footer />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;